import React, { useState, useEffect } from 'react';
import AuthGatedWorkflow from './components/AuthGatedWorkflow';
// import PDFViewer from './components/PDFViewer';  // Session management functionality
import Header from './components/Header';
import Footer from './components/Footer';
import { LicenseManagementDialog } from './components/LicenseManagementDialog';
import TrialRegistrationDialog from './components/TrialRegistrationDialog';
import GettingStartedGuide from './components/GettingStartedGuide';
import CancelTrialModal from './components/CancelTrialModal';
import DocumentationViewer from './components/DocumentationViewer';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { AuthProvider } from './contexts/AuthContext';
import { LicenseProvider } from './contexts/LicenseContext';
import { CapabilitiesProvider } from './contexts/CapabilitiesContext';
import { ArtifactGateProvider } from './components/ArtifactGate';
import { EditorResetBridge } from './contexts/EditorResetBridge';
import { VerificationLandingHandler } from './components/VerificationLandingHandler';

// Licensing Server URL - in production, this would come from environment or config
// NOTE: Create React App requires REACT_APP_ prefix for environment variables exposed to browser
const LICENSING_SERVER_URL = process.env.REACT_APP_LICENSING_SERVER_URL || 'http://localhost:1150';

/**
 * IS THERE A DOCUMENT ON THE BENCH? The one signal the page chrome keys on.
 *
 * The hero and the full footer exist to persuade a VISITOR; an AUTHOR with a
 * document open is paying for them in vertical space — on a 1080p display they
 * are literally half the page (EDITOR_WORKSPACE_PLAN.md §0). So the chrome
 * collapses while a document or session is active and returns on a fresh visit
 * or after Start Over.
 *
 * The signal is session state, not a prop threaded down from the workflow:
 * `uploadResult.documentId` is set by an upload and by a session load, and
 * `currentSessionId` covers a session adopted before any document fact lands.
 * Both are cleared by CLEAR_SESSION, which is what every way out — Home, Start
 * Over, a license switch — already dispatches. This is the SAME pair
 * PDFUpload's reset-mirror effect treats as "session cleared", deliberately:
 * one definition of "the author is working", read from both sides.
 *
 * The flip happens only on those transitions, when the workspace itself is
 * mounting or unmounting — never mid-edit, so the collapse cannot jump the
 * page under an author's cursor.
 */
function useDocumentOpen(): boolean {
  const { state } = useSession();
  return Boolean(state.uploadResult.documentId) || Boolean(state.currentSessionId);
}

/**
 * Renders its children only while NO document is open — the marketing chrome's
 * gate. A component rather than prop-threading because the providers already
 * wrap this tree: the signal is one hook call away wherever it is needed, and
 * App itself sits ABOVE SessionProvider and cannot ask.
 */
const FreshVisitOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return useDocumentOpen() ? null : <>{children}</>;
};

/**
 * The vertical band around the workspace: generous on a fresh visit, slim once
 * a document is open. The py-12 was part of the hero's breathing room; charged
 * against an open editor it is 3rem of nothing above the toolbar.
 */
const WorkspaceBand: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className={useDocumentOpen() ? 'py-3' : 'py-12'}>{children}</div>;
};

/**
 * The footer, slimmed to one line while a document is open. Terms and Privacy
 * stay reachable in BOTH states — legal reachability is an obligation, not
 * chrome — which is why the slim variant is a Footer mode rather than the
 * footer being hidden outright.
 */
const SessionAwareFooter: React.FC<{ onDocClick: (docPath: string) => void }> = ({ onDocClick }) => {
  return <Footer onDocClick={onDocClick} slim={useDocumentOpen()} />;
};

function App() {
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  const [showTrialRegistration, setShowTrialRegistration] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [currentDocPath, setCurrentDocPath] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Handle cancel trial from header
  const handleCancelTrialClick = () => {
    setShowCancelConfirm(true);
  };

  const handleDocClick = (docPath: string) => {
    setCurrentDocPath(docPath);
    setShowDocumentation(true);
  };

  const handleDocumentChange = (newPath: string) => {
    setCurrentDocPath(newPath);
  };

  // Handle license button click - always show management dialog first
  // The dialog will show options to start trial or enter existing key
  const handleLicenseClick = () => {
    setShowLicenseDialog(true);
  };

  // VerificationLandingHandler dispatches this after applying the license key
  // from a trial-welcome email link, so the user lands in the unified sign-in
  // dialog with the license already active.
  useEffect(() => {
    const openSignIn = () => setShowLicenseDialog(true);
    window.addEventListener('open-signin-dialog', openSignIn);
    return () => window.removeEventListener('open-signin-dialog', openSignIn);
  }, []);

  return (
    <AuthProvider>
      <LicenseProvider>
        <CapabilitiesProvider>
        <SessionProvider>
          <EditorResetBridge />
          <VerificationLandingHandler />
          <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50/20">
          <Header
            onLicenseClick={handleLicenseClick}
            onDocClick={handleDocClick}
            onCancelTrialClick={handleCancelTrialClick}
            onSignInClick={handleLicenseClick}
          />

          <main className="flex-grow">
            {/* Hero Section with improved styling */}
            <div className="relative overflow-hidden">
              {/* Background decoration — hero chrome, so it leaves with the hero.
                  Pulsing blobs behind a working editor are motion beside an
                  author's pixel work, with no visitor left to charm. */}
              <FreshVisitOnly>
              <div className="absolute inset-0 -z-10">
                <div className="absolute top-0 -left-4 w-72 h-72 bg-primary-300/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-soft"></div>
                <div className="absolute top-0 -right-4 w-72 h-72 bg-accent-300/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-soft animation-delay-2000"></div>
                <div className="absolute -bottom-8 left-20 w-72 h-72 bg-secondary-300/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-soft animation-delay-4000"></div>
              </div>
              </FreshVisitOnly>

              {/* THE CAP MOVED OFF THIS CONTAINER AND ONTO THE HERO TEXT.

                  It used to be `max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12` here,
                  which capped EVERYTHING below it at 1280px - including the PDF
                  editor. PDFUpload marks the editor `w-full` precisely so it can take
                  the window, and that had no effect at all from inside a 1280px box:
                  on a 2560px monitor the working area was still half the glass.

                  Scott, 18 Sep: "The PDF Viewer container does not need to be the same
                  width of the other windows higher in the page, this is a real working
                  area which needs breathing room."

                  Nothing else needed the cap. Every other child of this section
                  re-imposes its own - WorkflowChoice and UploadSection at max-w-4xl,
                  PDFUpload's progress row at max-w-6xl, its GenerationControls and
                  view-toggle rows at max-w-7xl - so removing it here changes only the
                  one element that asked to be wider. */}
              <WorkspaceBand>
                {/* THE HERO COLLAPSES WHILE A DOCUMENT IS OPEN (EDITOR_WORKSPACE_PLAN.md
                    §W1). Every word inside is owner-approved copy and none of it changes
                    here — only WHEN it shows. It returns in full on a fresh visit and
                    after Start Over, because at those moments the reader is a visitor
                    again. */}
                <FreshVisitOnly>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12 animate-fade-in">
                  {/*
                    ABOVE THE TITLE, because a scanner does not read sentences.

                    Someone arriving with an accessibility deadline scans for one word and
                    leaves if they do not find it. An adjective inside the subtitle is true
                    but invisible at scanning speed, so the claim gets its own object at the
                    top of the page where the eye lands first.

                    "Accessible fields" and not "accessible PDFs", here as everywhere else
                    on this page. The fields carry the author's accessible name, required
                    and read-only state, and a place in the reading order; the uploaded
                    document's own content is untouched and arrives as tagged, or untagged,
                    as it was. No standard named and no "compliant" — those wait on the ACR.
                  */}
                  {/*
                    Two claims, two pills, one per line (owner, 6 Oct) — each wraps as its
                    own unit instead of overflowing into the other. The first is the
                    accessibility claim, expanded from "Accessible fields, built in"
                    because the short form could be misread as "there is an accessibility
                    feature somewhere"; this wording puts the emphasis where the claim
                    actually lives. The second is the speed claim for the other half of
                    the product. Still "fields", still no standard named — the claims
                    discipline in LAUNCH_POSITIONING_AND_SERVICE.md applies here first.
                  */}
                  <div className="flex flex-col items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-2 rounded-full bg-success-50 px-4 py-1.5 text-sm font-semibold text-success-800 ring-1 ring-success-600/30">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Accessibility built into every field
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-semibold text-primary-800 ring-1 ring-primary-600/30">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                      Production servers in seconds
                    </span>
                  </div>
                  <h1 className="text-5xl md:text-6xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-700 via-primary-600 to-secondary-600 mb-4">
                    PDF Form Builder
                  </h1>
                  {/*
                    "ACCESSIBLE FILLABLE PDF FIELDS" — and the noun matters.

                    The fields are accessible: each carries the accessible name the author
                    typed, its required and read-only state, and a place in the reading
                    order, all asserted by tests over the emitted bytes. The DOCUMENT is
                    not, and this copy must never imply otherwise — an uploaded PDF's body
                    text reaches the output exactly as tagged, or untagged, as it arrived.
                    D38's correction measured 3,321 failed checks on a tagged source run
                    through generation, so "makes your PDF accessible" is a claim an
                    evaluator would disprove by opening one file.

                    No standard named and no "compliant" anywhere on this page. Those wait
                    on the ACR, whose scope paragraph still carries D38's corrected error.
                  */}
                  <p className="text-xl md:text-2xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
                    Accessible fillable PDF fields in minutes. Production servers in seconds.
                    <span className="block text-lg mt-2 text-neutral-500">
                      Weeks of manual effort reduced to a couple of hours — add accessible fields
                      to the forms you already have, and generate complete, deployment-ready
                      services that process flattened PDFs at scale.
                    </span>
                  </p>

                  {/* Trust indicators */}
                  <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-neutral-600">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Accessible fields in minutes, not hours
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Servers in seconds, not weeks
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      High-volume PDF processing
                    </div>
                  </div>
                </div>
                </FreshVisitOnly>

                {/* Main application. The wall is at the artifacts, not the door
                    (ANONYMOUS_EVALUATION_MODE.md section 1). */}
                <div className="w-full px-4 sm:px-6 lg:px-8 animate-slide-up animation-delay-200">
                  <ArtifactGateProvider
                    onStartTrial={() => setShowTrialRegistration(true)}
                    onOpenSignIn={handleLicenseClick}
                  >
                    <AuthGatedWorkflow
                      onStartTrial={() => setShowTrialRegistration(true)}
                      onOpenSignIn={handleLicenseClick}
                    />
                  </ArtifactGateProvider>
                </div>
              </WorkspaceBand>
            </div>
          </main>

          <SessionAwareFooter onDocClick={handleDocClick} />

          {/* License Management Dialog */}
          <LicenseManagementDialog
            open={showLicenseDialog}
            onClose={() => setShowLicenseDialog(false)}
            onRequestTrial={() => {
              setShowLicenseDialog(false);
              setShowTrialRegistration(true);
            }}
          />

          {/* Trial Registration Dialog */}
          <TrialRegistrationDialog
            isOpen={showTrialRegistration}
            onClose={() => setShowTrialRegistration(false)}
            onSuccess={() => setShowTrialRegistration(false)}
            licensingServerUrl={LICENSING_SERVER_URL}
            onEnterExistingKey={() => setShowLicenseDialog(true)}
          />

          {/* Cancel Trial Modal */}
          <CancelTrialModal
            isOpen={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            onCancellationSuccess={() => setShowCancelConfirm(false)}
          />

          {/* Documentation Viewer */}
          <DocumentationViewer
            isOpen={showDocumentation}
            onClose={() => setShowDocumentation(false)}
            documentPath={currentDocPath}
            onDocumentChange={handleDocumentChange}
          />

          {/* Stage-aware onboarding companion (non-modal, bottom-right) */}
          <GettingStartedGuide
            signInOpen={showLicenseDialog}
            trialOpen={showTrialRegistration}
            onOpenSignIn={handleLicenseClick}
            onStartTrial={() => setShowTrialRegistration(true)}
          />
        </div>
      </SessionProvider>
        </CapabilitiesProvider>
      </LicenseProvider>
    </AuthProvider>
  );
}

export default App;
